import Head from 'next/head';
import { useCallback, useEffect, useState } from 'react';
import * as topLoader from '@Helpers/topLoader';
import { useGetNominationForm } from '@Hooks/Api/nominations/useGetNominationForm';
import { useGetNominationMatches } from '@Hooks/Api/nominations/useGetNominationMatches';
import { useSubmitNomination } from '@Hooks/Api/nominations/useSubmitNomination';
import { PathParams } from '@App/paths';
import { useRouter } from '@Helpers/useRouter';
import { OrganizationAvatar, NotFound404 } from '@Components';
import { useTranslation } from 'react-i18next';
import { FormProvider, useForm } from 'react-hook-form';
import { debounce } from 'lodash';
import { Routes } from '@App/paths';

import styles from '@Views/NominationPage/NominationPage.module.scss';
import { NominationForm } from '@Views/NominationPage/NominationForm/NominationForm';
import { NominationMatchResult } from '@Types';
import { VotingPageLayout } from '@Components/AppLayout/VotingPageLayout';

const NominationFormPage = () => {
  const { t } = useTranslation('nominations');
  const { push, getSingleValueParam } = useRouter();
  const nominationSlug = getSingleValueParam(PathParams.NOMINATION_SLUG);
  const [getNominationForm, { data: nominationFormData }] = useGetNominationForm();
  const [
    getNominationMatches,
    {
      data: nominationMatches,
      setResponseData: setNominationMatches,
      loading: areNominationMatchesLoading,
    },
  ] = useGetNominationMatches();
  const [submitNomination, submittedData] = useSubmitNomination();
  const [has404, setHas404] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorCode, setErrorCode] = useState('');
  const methods = useForm();

  useEffect(() => {
    topLoader.start();
    getNominationForm(nominationSlug)
      .catch((error) => {
        if (error && error.status == 404) {
          setHas404(true);
        } else {
          throw error;
        }
      })
      .finally(() => {
        topLoader.done();
      });
  }, [getNominationForm, nominationSlug]);

  const fundName = nominationFormData?.fund_name;
  useEffect(() => {
    // Assume nominations are open unless we explicitly get false for the field.
    const nominationsClosed = nominationFormData?.nominations_open === false;
    if (nominationsClosed) {
      setErrorCode('NOMINATIONS_NOT_OPEN');
    }
  }, [nominationFormData, setErrorCode]);

  useEffect(() => {
    if (errorCode === 'NOMINATIONS_NOT_OPEN') {
      const closedRoute = Routes.NOMINATION_FORM_CLOSED(nominationSlug);
      push(closedRoute);
    }
  }, [errorCode, nominationSlug, push]);

  const handleNomineeNameChange = useCallback(
    (nomineeName: string) => {
      if (nomineeName && nomineeName.length > 2) {
        getNominationMatches(nominationSlug, nomineeName);
      } else {
        const emptyResult: NominationMatchResult = { matches: [] };
        setNominationMatches(emptyResult);
      }
    },
    [getNominationMatches, setNominationMatches, nominationSlug],
  );

  const onSubmit = methods.handleSubmit((data) => {
    const dataToSubmit: any = {};
    // Don't submit blanks
    for (const dataKey in data) {
      const value = data[dataKey];
      if (value === '') {
        dataToSubmit[dataKey] = undefined;
      } else {
        dataToSubmit[dataKey] = value;
      }
    }
    setIsSubmitting(true);
    submitNomination(nominationSlug, dataToSubmit)
      .then(() => {
        let thanksRoute = Routes.NOMINATION_FORM_THANKS(nominationSlug);
        if (nominationFormData && nominationFormData.custom_success_url) {
          thanksRoute = nominationFormData.custom_success_url;
        }
        push(thanksRoute);
      })
      .catch((result) => {
        setErrorCode('UNKNOWN');
        if (result.data && result.data.code) {
          setErrorCode(result.data.code);
        }
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  });

  const hideErrorModal = useCallback(() => {
    setErrorCode('');
  }, []);

  const nominationFormParams = {
    onSubmit,
    isSubmitting,
    errorCode,
    hideErrorModal: hideErrorModal,
    onNomineeNameChange: debounce(handleNomineeNameChange, 500),
    matches: nominationMatches?.matches || [],
    areMatchesLoading: areNominationMatchesLoading,
  };
  return (
    <VotingPageLayout avatar={nominationFormData?.fund_avatar_thumbnail}>
      <Head>
        <title>{t('page_title', { fund_name: fundName })}</title>
      </Head>
      {has404 && <NotFound404 />}
      {nominationFormData && (
        <div className={styles.container}>
          <h1 className={styles.mainHeader}>{t('heading', { fund_name: fundName })}</h1>
          <p className={styles.introText}>{t('page_intro')}</p>
          <p className={styles.introText}>{t('page_intro_2')}</p>
          <div className={styles.content}>
            <FormProvider {...methods}>
              <NominationForm nominationFormData={nominationFormData} {...nominationFormParams} />
            </FormProvider>
          </div>
        </div>
      )}
    </VotingPageLayout>
  );
};

export default NominationFormPage;
